import {createApp, PropType, ref} from 'vue';
import {connectVue} from '../modules/configureBugsnag';
import configureGlobalComponents from '../modules/configureGlobalComponents';
import axiosClient from '../modules/axiosClient';
import sendAnalyticsEvent from '../modules/sendAnalyticsEvent';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import BreakPoints from '../config/breakpoints';
import Gallery from '../components/global/gallery.vue';
import formatThousandsWithRounding from '../modules/formatThousandsWithRounding';
import MotHistory from '../components/mot-history.vue';
import CarSearchFinanceInfo from '../components/car-search/car-search-finance-info.vue';
import {ApiStarredVehicle} from '../../typings/api';
import useVuelidate from '@vuelidate/core';
import {email, helpers, maxLength, minLength, required, requiredIf, sameAs} from '@vuelidate/validators';
import {phone, postcode} from '../modules/regitValidators';

dayjs.extend(customParseFormat);

type financeTabOptions = 'total' | 'pcp' | 'hp';

const app = createApp({
    components: {
        'finance-info': CarSearchFinanceInfo,
        'gallery': Gallery,
        'mot-history': MotHistory,
    },
    props: {
        vehicleId: String,
        vehicleType: String,
        vehicleMake: String,
        vehicleModel: String,
        vehiclePrice: Number,
        vehicleMileage: Number,
        affiliate: String,
        leadSale: Boolean,
        leadUser: Object,
        hasFinance: Boolean,
        // modal finance info fields
        currentHpInfo: Object,
        currentPcpInfo: Object,
        hasEmailContact: Boolean,
        hasPhoneContact: Boolean,
        dealerTelephone: String,
        starredVehicle: Object as PropType<ApiStarredVehicle | null>,
        prefillUser: Object,
    },
    data() {
        return {
            // mobile only expand for text
            scrollPosition: 0,

            // enquiry
            enquireTitle: 'Enquire',
            loadingEnquire: false,
            enquireErrors: [] as String[],
            submitted: false,

            contactByEmail: false,
            isOutOfHours: false,
            enquiryContactType: '',
            contactDealerModalOpen: false,

            // toggle panels
            financeTab: 'total' as financeTabOptions,
            motActive: false,
            more: false, // has the read-more button been pressed

            containerWidth: 0,

            currentStarredVehicle: null as ApiStarredVehicle | null,
        };
    },
    computed: {
        toggleBottomBar(): boolean {
            return (this.scrollPosition > 450 && this.containerWidth <= BreakPoints.mobile) ||
            (this.scrollPosition > 650 && this.containerWidth > BreakPoints.tablet);
        },

        toggleBottomBarClosed(): boolean {
            return (this.scrollPosition <= 450 && this.containerWidth <= BreakPoints.mobile) ||
            (this.scrollPosition <= 650 && this.containerWidth > BreakPoints.tablet);
        },
        contactButtonText(): string {
            return this.hasPhoneContact ? 'Call Dealer' : 'Request a callback';
        },
        dealerNumber(): string {
            return this.contactDealerModalOpen ? this.dealerTelephone : '';
        },
    },

    mounted() {
        this.scroll();
        this.getScreenSize();
        window.addEventListener('scroll', this.scroll);
        window.addEventListener('resize', this.getScreenSize);

        this.chooseInitialTab();

        this.currentStarredVehicle = JSON.parse(JSON.stringify(this.starredVehicle));
    },
    setup(props) {
        const state = {
            firstname: ref( props.prefillUser.firstName || ''),
            surname: ref( props.prefillUser.lastName || ''),
            email: ref( props.prefillUser.email || ''),
            telephone: ref( props.prefillUser.telephone || ''),
            postcode: ref( props.prefillUser.postcode || ''),
            message: ref( ''),
            distance: ref(props.prefillUser.distance || ''),
        };

        const rules = {
            firstname: {
                required: helpers.withMessage('Please enter your name', required),
            },
            surname: {
                required: helpers.withMessage('Please enter your surname', required),
            },
            email: {
                required: helpers.withMessage('Please enter your email', required),
                email,
            },
            telephone: {
                required: helpers.withMessage('Please enter your telephone', required),
                phone: helpers.withMessage('Value is not a valid telephone', phone),
            },
            postcode: {
                required: helpers.withMessage('Please enter your postcode', required),
                postcode: helpers.withMessage('Value is not a valid postcode', postcode),
            },
            message: {
                // required: helpers.withMessage('Please enter your message', required),
            },
        };

        const v = useVuelidate(rules, state);

        return {
            firstname: state.firstname,
            surname: state.surname,
            email: state.email,
            telephone: state.telephone,
            postcode: state.postcode,
            message: state.message,
            distance: state.distance,

            v$: v,
        };
    },
    methods: {
        toggleStarred() {
            if (!window.userLoggedIn) {
                const currentPageUrl = window.location.pathname;

                window.location.href = '/login?referring_url=' + btoa(currentPageUrl);
                return;
            }

            if (this.currentStarredVehicle !== null) {
                this.unStarVehicle();
            } else {
                this.starVehicle();
            }
        },
        starVehicle() {
            axiosClient.post('/api/starred-vehicles', {
                type: 'used',
                externalId: this.vehicleId,
            }).then((result) => {
                this.currentStarredVehicle = result.data as ApiStarredVehicle;
            }).catch(() => {
                console.log('error starring vehicle');
            });
        },
        unStarVehicle() {
            if (!this.currentStarredVehicle) {
                return;
            }

            axiosClient.delete('/api/starred-vehicles/' + this.currentStarredVehicle.id).then((result) => {
                this.currentStarredVehicle = null;
            }).catch(() => {
                console.log('error un-starring vehicle');
            });
        },
        contactButtonAction() {
            return this.openContactDealerModal();
        },
        chooseInitialTab() {
            if (this.currentPcpInfo !== null) {
                this.goToTab('pcp');
            } else if (this.currentHpInfo !== null) {
                this.goToTab('hp');
            }
        },
        scroll() {
            this.scrollPosition = this._getScrollPosition();
        },
        _getScrollPosition() {
            // for Chrome, Firefox and Safari
            if (typeof window.scrollY !== 'undefined') {
                return window.scrollY;
            }

            // for IE because it's awkward like that
            return document.documentElement.scrollTop;
        },
        getScreenSize() {
            this.containerWidth = window.innerWidth;
        },

        contactDealer() {
            this.$modal.show('uc-enquiryForm');

            this.enquiryContactType = 'message';
            this.enquireTitle = 'Contact Dealer';
        },

        messageDealer() {
            this.$modal.show('uc-enquiryForm');

            this.enquiryContactType = 'message';
            this.enquireTitle = 'Message Dealer';
        },

        callbackDealer() {
            this.$modal.show('uc-enquiryForm');

            this.enquiryContactType = 'callback';
            this.enquireTitle = 'Request Callback';
        },

        async enquire() {
            const validationResult = await this.v$.$validate();
            if (validationResult) {
                // prevent double submit
                if (this.loadingEnquire) {
                    return;
                }
                this.loadingEnquire = true;
                const payload = {
                    'type': this.vehicleType,
                    'carID': this.vehicleId,
                    'affiliate': this.affiliate,

                    'firstName': this.firstname,
                    'lastName': this.surname,
                    'email': this.email,
                    'telephone': this.telephone,
                    'postcode': this.postcode,
                    'message': this.message,
                    'contactByEmail': this.contactByEmail,
                    'enquiryContactType': this.enquiryContactType,
                    'offer': null as any,
                    'distance': this.distance,
                };

                axiosClient.post('/api/buying/enquiry', payload).then(() => {
                    this.submitted = true;

                    if (!this.leadSale) {
                        window.onFacebookPixelLoaded(() => {
                            window.fbq('track', 'Lead', {
                                content_category: 'Vehicle Enquire',
                                content_name: this.vehicleType.toLowerCase() + ' car',
                            });
                        });

                        if (this.vehicleType.toLowerCase() === 'used') {
                            sendAnalyticsEvent('Conversion Tracking', 'Used Cars', 'Enquire');
                        } else if (this.vehicleType.toLowerCase() === 'new') {
                            sendAnalyticsEvent('Conversion Tracking', 'New Cars', 'Enquire');
                        }
                    }
                }, (response) => {
                    const apiErrors = response.response.data.errors || {};
                    const newErrors = [] as String[];

                    Object.keys(apiErrors).forEach((field) => {
                        const errors = apiErrors[field];
                        errors.forEach((error: string) => {
                            newErrors.push(error);
                        });
                    });

                    this.enquireErrors = newErrors;
                }).catch((error) => {
                    this.enquireErrors = [error.toString()];
                }).finally(() => {
                    this.loadingEnquire = false;
                });
            }
        },

        formatThousandsWithRounding,

        toggleMore() {
            this.more = !this.more;
        },

        toggleMotHistory() {
            this.motActive = (!this.motActive);
        },

        openContactDealerModal() {
            this.$modal.show('uc-contact');
            this.contactDealerModalOpen = true;
            sendAnalyticsEvent('Conversion Tracking', 'Used Cars', 'View phone');
        },

        closeContactDealerModal() {
            this.$modal.hide('uc-contact');
            this.contactDealerModalOpen = false;
        },

        showFinanceInfo() {
            this.$modal.show('modal-finance-info', {pcp: this.currentPcpInfo, hp: this.currentHpInfo});
        },

        goToTab(tab: financeTabOptions) {
            this.financeTab = tab;
        },
    },
}, window.propsData);

let initializationAttempts = 0;
const MAX_ATTEMPTS = 3;

function initializeCalculator() {
    const container = document.getElementById('cf247-calculator-container');
    const existingScript = document.getElementById('cf247-calculator-script');

    if (container && !existingScript && initializationAttempts < MAX_ATTEMPTS) {
        initializationAttempts++;

        try {
            const script = document.createElement('script');
            script.id = 'cf247-calculator-script';
            script.src = 'https://partner-calculator.carfinance247.co.uk/widgets/calculator.js';
            script.setAttribute('data-aid', '43745');
            script.setAttribute('data-utms', '&utm_medium=referral&utm_source=regit&utm_campaign=calculator');

            if (window.propsData.vehiclePrice) {
                script.setAttribute('data-amount', window.propsData.vehiclePrice.toString());
            }
            if (window.propsData.vehicleMileage) {
                script.setAttribute('data-mileage', window.propsData.vehicleMileage.toString());
            }

            script.async = true;

            script.addEventListener('load', () => {
                container.dataset.initialized = 'true';
            });

            script.addEventListener('error', () => {
                console.error('Failed to load calculator script');
                script.remove();
                setTimeout(initializeCalculator, 1000);
            });

            container.appendChild(script);
        } catch (error) {
            console.error('Error initializing calculator:', error);
        }
    }
}

window.addEventListener('DOMContentLoaded', initializeCalculator);

connectVue(app);
configureGlobalComponents(app);
app.mount('#app');
